import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { useUserSearch } from '../hooks/useUserSearch';
import { impersonationApi, type ImpersonationCandidate } from '../api/impersonationApi';
import { beginImpersonation } from '../utils/impersonation';

interface ImpersonationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Super-admin-only dialog: search a user (autocomplete after 3 chars) and impersonate them.
 * Selecting a user starts the session and hard-reloads the app as that user.
 */
const ImpersonationDialog = ({ open, onOpenChange }: ImpersonationDialogProps) => {
  const { t } = useTranslation();
  const { query, setQuery, results, loading, error, hasEnoughChars, minChars } = useUserSearch();
  const [startingId, setStartingId] = useState<number | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const handleSelect = async (user: ImpersonationCandidate) => {
    setStartError(null);
    setStartingId(user.id);
    try {
      const { impersonationToken, target } = await impersonationApi.start(user.id);
      beginImpersonation({ token: impersonationToken, target }); // hard-reloads the app
    } catch (e) {
      setStartError(e instanceof Error ? e.message : t('Failed to start impersonation'));
      setStartingId(null);
    }
  };

  const isStarting = startingId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Impersonate a user')}</DialogTitle>
          <DialogDescription>
            {t('Search a user by name or email to view the app as them.')}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search a user…')}
            className="pl-8"
          />
        </div>

        <div className="max-h-72 min-h-[3rem] overflow-y-auto">
          {!hasEnoughChars && (
            <p className="px-1 py-2 text-sm text-muted-foreground">
              {t('Type at least {{count}} characters', { count: minChars })}
            </p>
          )}

          {hasEnoughChars && loading && (
            <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('Searching…')}
            </div>
          )}

          {hasEnoughChars && !loading && !error && results.length === 0 && (
            <p className="px-1 py-2 text-sm text-muted-foreground">{t('No user found')}</p>
          )}

          {error && <p className="px-1 py-2 text-sm text-destructive">{error}</p>}

          <ul className="flex flex-col">
            {results.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(user)}
                  disabled={isStarting}
                  className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{user.username}</span>
                    <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                  </div>
                  {startingId === user.id ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                      {user.role}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {startError && <p className="text-sm text-destructive">{startError}</p>}
      </DialogContent>
    </Dialog>
  );
};

export default ImpersonationDialog;
